import { useContext, useState } from 'react';
import FieldContext from '../../../FieldContext';
import AddLogEntryButton from './AddLogEntryButton';
import NewLogEntryForm from './NewLogEntryForm';
import useApi from '@/lib/api/useApi';
import PortfolioItemContext from '@/components/ChallengePortfolio/NormalView/portfolio-item/PortfolioItemContext';
import type {
  DatabaseExternalFile,
  ExternalFile,
  LogEntry,
  LogEntryWithoutFiles,
  UploadedImage,
} from '@/lib/types';
import type {
  KeyedExternalFile,
  KeyedImage,
} from './NewLogEntryForm/NewLogEntryFiles';
import HttpMethod from '@/lib/constants/HttpMethod.enum';
import downsizeImage from '@/components/FileAttachments/AddFiles/useUploadImages/downsizeImage';
import { MAX_FILES_PER_UPLOAD } from '@/components/FileAttachments/AddFiles/AddFilesControls/ImageUpload/ImageUploadOption/getFilesToUpload';

type AddLogEntryProps = {
  logEntryName: string;
};

const AddLogEntry = ({ logEntryName }: AddLogEntryProps) => {
  const api = useApi();

  const { setPortfolioItem, portfolioItemApiEndpoint } =
    useContext(PortfolioItemContext);
  const field = useContext(FieldContext);

  const [newLogEntryFormOpen, setNewLogEntryFormOpen] = useState(false);

  const saveNewLogEntry = async (
    logEntryContent: string | null,
    attachedImages: Map<string, KeyedImage>,
    attachedExternalFiles: Map<string, KeyedExternalFile>,
  ): Promise<void> => {
    if (
      logEntryContent === null &&
      attachedImages.size === 0 &&
      attachedExternalFiles.size === 0
    ) {
      throw new Error(
        'Cannot save log entry that has no content, no images and no external files.',
      );
    }

    const logEntry: LogEntry = {
      ...(await api.post<LogEntryWithoutFiles>(
        `${portfolioItemApiEndpoint}/log-entries?fieldId=${field.id}`,
        {
          content: logEntryContent ?? '',
        },
      )),
      images: {},
      externalFiles: {},
    };

    if (attachedImages.size > 0) {
      const formData = new FormData();

      const imagesToUpload = Array.from(attachedImages.values()).slice(
        0,
        MAX_FILES_PER_UPLOAD,
      );

      const downsizedImageFiles = await Promise.all(
        imagesToUpload.map((keyedImage: KeyedImage): Promise<File> =>
          downsizeImage(keyedImage.imageFile),
        ),
      );

      for (const imageFile of downsizedImageFiles) {
        formData.append('images', imageFile);
      }

      const images = await api.call<Record<number, UploadedImage>>(
        `${portfolioItemApiEndpoint}/log-entries/${logEntry.id}/images`,
        {
          method: HttpMethod.POST,
          body: formData,
        },
      );

      logEntry.images = images;
    }

    if (attachedExternalFiles.size > 0) {
      const externalFiles = await api.post<
        Record<number, DatabaseExternalFile>
      >(
        `${portfolioItemApiEndpoint}/log-entries/${logEntry.id}/external-files`,
        {
          externalFiles: Array.from(attachedExternalFiles.values()).map(
            (attachedExternalFile: KeyedExternalFile): ExternalFile =>
              attachedExternalFile.externalFile,
          ),
        },
      );

      logEntry.externalFiles = externalFiles;
    }

    setNewLogEntryFormOpen(false);

    setPortfolioItem(portfolioItem => {
      portfolioItem.logEntries[logEntry.id] = logEntry;
    });
  };

  if (!newLogEntryFormOpen) {
    return (
      <AddLogEntryButton
        logEntryName={logEntryName}
        onClick={() => setNewLogEntryFormOpen(true)}
      />
    );
  }

  return (
    <NewLogEntryForm
      logEntryName={logEntryName}
      onSave={saveNewLogEntry}
      onCancel={() => setNewLogEntryFormOpen(false)}
    />
  );
};

export default AddLogEntry;
